import React, { useMemo, useState } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useHttpContext } from '@/common/hooks/HttpContext';
import { toasts } from '@/common/toasts';
import { Maybe, OrganizationLocation, ScanSessionAdminRow } from '@/common/types';
import { Button } from '@/components/common/button/button';
import { resultIsError } from '@/services/HttpService';

interface RelocateSessionModalProps {
    session: ScanSessionAdminRow;
    organizationLocations: OrganizationLocation[];
    onClose: () => void;
    onSuccess: () => void;
}

export const RelocateSessionModal = (props: RelocateSessionModalProps) => {
    const { session, organizationLocations, onClose, onSuccess } = props;
    const { httpService } = useHttpContext();
    const { successToast } = toasts;

    const locations = useMemo(
        () => organizationLocations.find((org) => org.id === session.organizationId)?.locations ?? [],
        [organizationLocations, session.organizationId],
    );

    const [selectedLocationId, setSelectedLocationId] = useState<Maybe<number>>(session.locationId ?? undefined);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<Maybe<string>>(undefined);

    const handleSubmit = async () => {
        if (selectedLocationId === undefined) {
            return;
        }
        setIsSubmitting(true);
        setError(undefined);
        const res = await httpService.adminRelocateScanSession(session.sessionUUID, selectedLocationId);
        if (!res || resultIsError(res)) {
            setError(res?.message || 'Could not relocate session.');
            setIsSubmitting(false);
            return;
        }
        setIsSubmitting(false);
        successToast('Session relocated.');
        onSuccess();
    };

    const isUnchanged = selectedLocationId === session.locationId;

    return (
        <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ textAlign: 'center' }}>Relocate Session</DialogTitle>
            <DialogContent>
                <Typography variant="body2" component="div" sx={{ mb: 2, fontSize: '16px' }}>
                    <div>
                        <strong>Organization: </strong>
                        {session.organizationName}
                    </div>
                    <div>
                        <strong>Assessment: </strong>
                        {session.assessmentName}
                    </div>
                    <div>
                        <strong>Provider: </strong>
                        {session.practitionerName}
                    </div>
                    <div>
                        <strong>Created: </strong>
                        {session.createdDate
                            ? `${DateTime.fromISO(session.createdDate).toLocaleString(
                                  DateTime.DATE_SHORT,
                              )} ${DateTime.fromISO(session.createdDate).toLocaleString(DateTime.TIME_WITH_SECONDS)}`
                            : ''}
                    </div>
                </Typography>
                <FormControl fullWidth sx={{ mt: 1 }}>
                    <InputLabel id="relocate-location-label">Location</InputLabel>
                    <Select
                        labelId="relocate-location-label"
                        label="Location"
                        value={selectedLocationId ?? ''}
                        onChange={(e) => setSelectedLocationId(Number(e.target.value))}
                        disabled={isSubmitting}
                    >
                        {locations.map((location) => (
                            <MenuItem key={location.id} value={location.id}>
                                {location.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {error && (
                    <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                        {error}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
                <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting || isUnchanged || selectedLocationId === undefined}
                >
                    Relocate Session
                </Button>
            </DialogActions>
        </Dialog>
    );
};
